import React, { useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { SERVICES, SERVICE_CATEGORIES } from "../data/services";

const NAV = [
  { to: "/", label: "Home", testid: "nav-home" },
  { to: "/about", label: "About", testid: "nav-about" },
];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();
  const onServices = location.pathname.startsWith("/services");

  return (
    <header
      className="sticky top-0 z-50 bg-white border-b border-[#E5E7EB]"
      data-testid="site-header"
    >
      {/* Top gold rule */}
      <div className="h-[3px] w-full nnc-gold-bg" />

      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex items-center justify-between h-20">
          {/* Logo + wordmark */}
          <Link
            to="/"
            className="flex items-center gap-3 group"
            data-testid="header-logo-link"
          >
            <img
              src="/assets/nnc-logo.svg"
              alt="NNC Medallion"
              className="h-12 w-12"
              data-testid="header-logo-medallion"
            />
            <div className="hidden sm:flex flex-col leading-tight">
              <span
                className="font-bold tracking-tight nnc-text"
                style={{ fontSize: "0.95rem" }}
                data-testid="header-wordmark-line1"
              >
                Nonprofit Navigation
              </span>
              <span
                className="font-bold tracking-tight nnc-text"
                style={{ fontSize: "0.95rem" }}
                data-testid="header-wordmark-line2"
              >
                Consulting, LLC
              </span>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-10">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                className={({ isActive }) =>
                  `nnc-nav-link ${isActive ? "is-active" : ""}`
                }
                data-testid={n.testid}
              >
                {n.label}
              </NavLink>
            ))}

            {/* Services dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setServicesOpen(true)}
              onMouseLeave={() => setServicesOpen(false)}
            >
              <NavLink
                to="/services"
                className={`nnc-nav-link inline-flex items-center gap-1 ${onServices ? "is-active" : ""}`}
                data-testid="nav-services"
              >
                Services <ChevronDown className="h-3 w-3" strokeWidth={3} />
              </NavLink>

              {servicesOpen && (
                <div
                  className="absolute right-0 top-full pt-3 w-[640px]"
                  data-testid="services-dropdown"
                >
                  <div className="bg-white border border-[#00205B] shadow-xl">
                    <div className="h-[3px] w-full nnc-gold-bg" />
                    <div className="grid grid-cols-2 gap-x-6 gap-y-1 p-6">
                      {SERVICES.map((s) => (
                        <Link
                          key={s.slug}
                          to={`/services/${s.slug}`}
                          className="group flex items-start gap-3 py-2 hover:bg-[#FBF2DE] -mx-2 px-2 transition-colors"
                          data-testid={`services-dropdown-link-${s.slug}`}
                        >
                          <span
                            className="nnc-gold-text font-bold text-xs pt-1 tracking-widest"
                            style={{ minWidth: "1.6rem" }}
                          >
                            {s.number}
                          </span>
                          <span className="nnc-text font-bold text-sm leading-snug">
                            {s.title}
                          </span>
                        </Link>
                      ))}
                    </div>
                    <div className="border-t border-[#E5E7EB] px-6 py-3 flex items-center justify-between bg-[#FBF2DE]">
                      <span className="nnc-overline">{SERVICES.length} Practice Areas</span>
                      <Link
                        to="/services"
                        className="nnc-text font-bold text-xs uppercase tracking-widest hover:underline"
                        data-testid="services-dropdown-view-all"
                      >
                        View Full Directory →
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <NavLink
              to="/contact"
              className={({ isActive }) =>
                `nnc-nav-link ${isActive ? "is-active" : ""}`
              }
              data-testid="nav-contact"
            >
              Contact
            </NavLink>
          </nav>

          {/* CTA + mobile toggle */}
          <div className="flex items-center gap-3">
            <Link
              to="/services"
              className="hidden lg:inline-flex nnc-btn"
              data-testid="header-cta-services"
            >
              View Services
            </Link>
            <button
              type="button"
              className="lg:hidden p-2 nnc-text"
              onClick={() => setOpen(!open)}
              aria-label="Toggle menu"
              data-testid="header-mobile-toggle"
            >
              {open ? <X /> : <Menu />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden border-t border-[#E5E7EB] bg-white" data-testid="mobile-menu">
          <div className="px-6 py-4 flex flex-col">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `py-3 border-b border-[#E5E7EB] nnc-nav-link ${isActive ? "is-active" : ""}`
                }
                data-testid={`mobile-${n.testid}`}
              >
                {n.label}
              </NavLink>
            ))}
            <NavLink
              to="/services"
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `py-3 border-b border-[#E5E7EB] nnc-nav-link ${isActive ? "is-active" : ""}`
              }
              data-testid="mobile-nav-services"
            >
              Services
            </NavLink>
            <NavLink
              to="/contact"
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `py-3 border-b border-[#E5E7EB] nnc-nav-link ${isActive ? "is-active" : ""}`
              }
              data-testid="mobile-nav-contact"
            >
              Contact
            </NavLink>
            <Link
              to="/services"
              onClick={() => setOpen(false)}
              className="nnc-btn mt-4"
              data-testid="mobile-cta-services"
            >
              View Services
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
